import React from 'react';
import {NavLink} from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@mui/material/IconButton';
import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineOppositeContent from '@material-ui/lab/TimelineOppositeContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import SportsIcon from '@material-ui/icons/Sports';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import EventIcon from '@material-ui/icons/Event';
import LaptopMacIcon from '@material-ui/icons/LaptopMac';
import PaymentIcon from '@material-ui/icons/Payment';
import RefreshIcon from '@material-ui/icons/Refresh';
import Typography from '@material-ui/core/Typography';
import { formatDate } from '../../util/date';
import { formatMoney } from '../account/TransactionView';

const useStyles = makeStyles((theme: any) => ({
    paper: {
        padding: '6px 16px',
    },
    secondaryTail: {
        backgroundColor: theme.palette.secondary.main,
    },
}));

//enum with the types of events
export enum EventType {
    BET_LOG = "betlog",
    CREDIT = "credit",
    TRANSACTION = "transaction",
    DEPOSIT = "deposit"
}

export interface Event {
    datetime: Date;
    type: EventType;
    amount?: number;
    currency?: string;
    id: number;
    userId?: number;
    description?: string;
    selected?: boolean;
}


interface EventTimeLineProps {
    mergedData: any[];
    displayDetails: (event: Event)=>{};
    loadMoreItems: ()=>{};
    isLoading: boolean;
}

interface EventTimeLineState {
    currentItems: Event[];
}

//This component displays the timeline of the event.
//It displays the date and time of the event, the type of event and the amount.
//It uses infinite scroll to load more items.
export class EventTimeLine extends React.Component<EventTimeLineProps, EventTimeLineState>{

    classes = useStyles;

    constructor(props: EventTimeLineProps) {
        super(props);
        this.state = { currentItems: this.props.mergedData };
    }

    //This function is used to display the details of the event that was clicked on.
    //It displays the details on the right side of the screen.
    //It also highlights the event that was clicked on.
    displayDetails(event: any) {
        console.log("display details for event ", event);
        this.props.displayDetails(event);
        let currentItems = this.state.currentItems;
        currentItems.forEach((item) => {
            item.selected = false;
        });
        event.selected = true;
        this.setState({ currentItems: currentItems });
    }

    //This function is used to load more items when the user scrolls to the bottom of the page.
    loadMoreItems() {
        console.log("load more items");
        if (!this.props.isLoading) this.props.loadMoreItems();
    }

    //This function is used to choose the icon to display based on the type of event.
    chooseTimeLineDot(event: Event) {
        if (event.type === EventType.BET_LOG) {
            return <TimelineDot color="primary" variant="outlined" style={{cursor:"pointer"}} onClick={(e: any)=>{ this.displayDetails(event) }} >
                    <SportsIcon color="primary"/>
                </TimelineDot>;
        } else if (event.type === EventType.CREDIT) {
            return <TimelineDot color="secondary" variant="outlined" style={{cursor:"pointer"}} onClick={(e: any)=>{ this.displayDetails(event) }} >
                    <PaymentIcon color="secondary" />
                    </TimelineDot>;
        } else if (event.type === EventType.TRANSACTION) {
            return <TimelineDot color="primary" style={{cursor:"pointer"}} onClick={(e: any)=>{ this.displayDetails(event) }} >
                    <MonetizationOnIcon />
                </TimelineDot>;
        } else {
            return <TimelineDot color="primary" style={{cursor:"pointer"}} onClick={(e: any)=>{ this.displayDetails(event) }} ><LaptopMacIcon />
                </TimelineDot>;
        }
    }

    getLink(value: string | number, type : EventType){
        let path = "";
        switch(type){
            case EventType.CREDIT:
                path = "/credit/";
                break;
            case EventType.TRANSACTION:
                path = "/transaction/";
                break;
            case EventType.BET_LOG:
                path = "/sportsbetting/bet-log/";
                break;
            default:
                path = "";
        }
        return <NavLink to={path+value}>{value}</NavLink>
    }

    renderDescription(event: Event) {
        if (event.type === EventType.BET_LOG) {
            return <>
                <Typography>Bet Log {this.getLink(event.id, event.type)} with amount {event.amount}</Typography>
                <Typography>{event.description}</Typography>
            </>
        } else if (event.type === EventType.CREDIT) {
            return <>
                <Typography>Credit {this.getLink(event.id, event.type)} with amount {event.amount}</Typography>
                <Typography>{event.description}</Typography>
            </>
        } else if (event.type === EventType.TRANSACTION) {
            return <>
                <Typography>Transaction {this.getLink(event.id, event.type)} with amount {formatMoney({amount: event.amount})}</Typography>
                <Typography>{event.description}</Typography>
            </>
        } else {
            return <Typography>Amount: {event.amount}</Typography>
        }
    }

    render() {
        const currentItems = this.state.currentItems;
        const isLoading = this.props.isLoading;
        let previousDate = new Date();

        return (
            <Timeline align="left">
                {currentItems.map((event: Event, index: number) => {
                    return (
                        <>
                            {event.datetime.getDate() !== previousDate.getDate() ? (
                                previousDate = event.datetime,
                                <TimelineItem>
                                    <TimelineOppositeContent style={{ flex: 0.15 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {formatDate(event.datetime).split(' ')[0]}
                                        </Typography>
                                    </TimelineOppositeContent>
                                    <TimelineSeparator>
                                        <TimelineDot>
                                            <EventIcon/>
                                        </TimelineDot>
                                        <TimelineConnector />
                                    </TimelineSeparator>
                                    <TimelineContent>
                                    </TimelineContent>
                                </TimelineItem>
                            ) : <></>}
                            <TimelineItem>
                                <TimelineOppositeContent key={index} style={{ flex: 0.15 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {event.datetime.toTimeString().split(' ')[0]}
                                    </Typography>
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    {this.chooseTimeLineDot(event)}
                                    <TimelineConnector />
                                </TimelineSeparator>
                                <TimelineContent>
                                    <Typography variant="body2" color="textSecondary">
                                        {this.renderDescription(event)}
                                    </Typography>
                                </TimelineContent>
                            </TimelineItem>
                        </>
                    );
                })}
                <TimelineItem>
                    <TimelineSeparator>
                        {!isLoading ? (
                            <TimelineDot color="secondary">
                                <RefreshIcon style={{cursor:"pointer"}} onClick={(event: any)=>{ this.loadMoreItems() }} /> 
                            </TimelineDot>
                        ) : (
                            <TimelineDot color="disabled">
                                <RefreshIcon /> 
                            </TimelineDot>
                        )}
                    </TimelineSeparator>
                    <TimelineContent>
                    </TimelineContent>
                </TimelineItem>
            </Timeline>
        );
    }
}